import { useEffect, useSyncExternalStore } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";

import { isInstalled } from "./components/install-hint";
import { Shell } from "./components/shell";
import { activeOrg, auth } from "./lib/auth";
import { appBase } from "./lib/env";
import { startOutbox } from "./outbox/outbox";
import { CatalogScreen } from "./screens/catalog";
import { DesignScreen } from "./screens/design";
import { HomeScreen } from "./screens/home";
import { IntroScreen } from "./screens/intro";
import { LoginScreen } from "./screens/login";
import { canSurvey, NoAccessScreen } from "./screens/no-access";
import { OutboxScreen } from "./screens/outbox";
import { ProjectScreen } from "./screens/project";
import { RenderScreen } from "./screens/render";
import { RoomScreen } from "./screens/room";
import { SendScreen } from "./screens/send";
import { SignScreen } from "./screens/sign";
import { SiteScreen } from "./screens/site";

export function App() {
  const state = useSyncExternalStore(auth.subscribe, auth.get);
  const signedIn = state.status === "signed_in";
  // Restart the sender for each sign-in and organisation: it only sends that owner's revisions.
  const owner = state.status === "signed_in" ? `${state.session.user.id}:${state.session.active_org_id ?? ""}` : null;
  useEffect(() => (owner ? startOutbox() : undefined), [owner]);
  if (state.status === "loading") return null;
  if (state.status === "signed_in" && !canSurvey(activeOrg(state.session))) return <NoAccessScreen session={state.session} />;
  return (
    <BrowserRouter basename={appBase.replace(/\/$/, "")}>
      {signedIn ? (
        <Routes>
          <Route element={<Shell />}>
            <Route index element={<HomeScreen />} />
            <Route path="catalog" element={<CatalogScreen />} />
            <Route path="outbox" element={<OutboxScreen />} />
            <Route path="p/:id" element={<ProjectScreen />} />
            <Route path="p/:id/site" element={<SiteScreen />} />
            <Route path="p/:id/design" element={<DesignScreen />} />
            <Route path="p/:id/sign" element={<SignScreen />} />
            <Route path="p/:id/send" element={<SendScreen />} />
          </Route>
          {/* Full-screen work surfaces: no rail. */}
          <Route path="p/:id/rooms/:roomId" element={<RoomScreen />} />
          <Route path="p/:id/render" element={<RenderScreen />} />
          <Route path="login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : (
        <Routes>
          {/* A browser visitor lands on the introduction; the installed app and an expired session go straight to sign-in. */}
          <Route index element={state.reason === "expired" || isInstalled() ? <LoginScreen expired={state.reason === "expired"} /> : <IntroScreen />} />
          <Route path="login" element={<LoginScreen expired={state.reason === "expired"} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </BrowserRouter>
  );
}
